import { AuthenticationResult, EventMessage, EventType, LogLevel, PublicClientApplication, Configuration } from '@azure/msal-browser';
import config from '../config';
import configIndex from './index';

export const aadClientId = process.env.NEXT_PUBLIC_AAD_CLIENT_ID ?? '838a95b4-eec5-4c23-b521-df4e09e6d2e2';
export const aadTenantId = process.env.NEXT_PUBLIC_AAD_TENANT_ID ?? '80267ce2-681e-47b6-998c-12727c6fe96e';
export const aadAuthority = process.env.NEXT_PUBLIC_AAD_AUTHORITY ?? `https://login.microsoftonline.com/${aadTenantId}/`;
export const redirectUri = typeof window !== 'undefined' ? window.location.origin : '/';
export const postLogoutRedirectUri = typeof window !== 'undefined' ? window.location.origin : '/';

// MSAL configuration
export const msalConfig: Configuration = {
	auth: {
		clientId: aadClientId,
		authority: aadAuthority,
		redirectUri,
		postLogoutRedirectUri,
		navigateToLoginRequestUrl: false,
	},
	cache: {
		cacheLocation: 'sessionStorage',
		storeAuthStateInCookie: false,
	},
	system: {
		loggerOptions: {
			loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
				if (containsPii) {
					return;
				}
				switch (level) {
					case LogLevel.Error:
						console.error(message);
						return;
					case LogLevel.Info:
						console.info(message);
						return;
					case LogLevel.Verbose:
						console.debug(message);
						return;
					case LogLevel.Warning:
						console.warn(message);
						return;
					default:
						return;
				}
			},
			logLevel: LogLevel.Info,
		},
	},
};

// Resolve optional custom scopes from env (space-delimited)
const envScope = (process.env.NEXT_PUBLIC_SCOPE ?? '').trim();

// Microsoft Graph scopes (for Graph API - no custom roles)
const getGraphScopes = () => {
	return ['User.Read', 'Files.Read.All', 'Sites.Read.All', 'ExternalItem.Read.All'];
};

// Custom API scopes (prefer env SCOPE, else use app default)
const getCustomApiScopes = () => {
	if (envScope) {
		return envScope.split(' ').filter(scope => scope.trim());
	}
	return [`${aadClientId}/.default`];
};

export const loginRequest = {
	scopes: [
		...getGraphScopes(),
		'openid',
		'profile',
		'offline_access',
		...(envScope ? envScope.split(' ').filter(s => s.trim()) : []),
		`api://localhost:3000/${aadClientId}/access_as_user`,
	].filter(Boolean as any),
};

// Microsoft Graph request (for Graph API calls)
export const graphRequest = {
	scopes: getGraphScopes(),
};

// Custom API request (for APIM calls - includes custom roles)
export const customApiRequest = {
	scopes: getCustomApiScopes(),
};

// Default API request (use Graph for most cases)
export const apiRequest = {
	scopes: getCustomApiScopes(),
};

// Silent token request for API calls
export const silentRequest = {
	scopes: apiRequest.scopes,
	account: null as any,
};

const msalInstance = new PublicClientApplication(msalConfig);
let isMsalInitialized = false;

export const getMsalInitializationStatus = () => isMsalInitialized;

export const initializeMsalInstance = async () => {
	try {
		// Initialize the MSAL instance first
		await msalInstance.initialize();
		isMsalInitialized = true;

		// Handle redirect promise after initialization
		try {
			const redirectResponse = await msalInstance.handleRedirectPromise();
			if (redirectResponse) {
				// Handle successful redirect response
			}
		} catch (error) {
			console.error('Error handling redirect promise during initialization:', error);
		}

		// Default to using the first account if no account is active on page load
		if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
			const accounts = msalInstance.getAllAccounts();
			const accountInfo = accounts[0]; // Use first account
			msalInstance.setActiveAccount(accountInfo);
		}

		// Listen for sign-in event and set active account
		msalInstance.addEventCallback((message: EventMessage) => {
			if (message.eventType === EventType.LOGIN_SUCCESS && message?.payload) {
				const payload = message.payload as AuthenticationResult;

				if (payload?.account) {
					msalInstance.setActiveAccount(payload?.account);
				}
			}

			if (message.eventType === EventType.HANDLE_REDIRECT_START) {
				console.log('MSAL: Starting to handle redirect');
			}

			if (message.eventType === EventType.HANDLE_REDIRECT_END) {
				console.log('MSAL: Finished handling redirect');
			}

			if (message.eventType === EventType.HANDLE_REDIRECT_END && message.error) {
				console.error('MSAL: Error handling redirect:', message.error);
				// If there's an interaction_required error, redirect to login
				const error = message.error as any;
				if (error.errorCode === 'interaction_required' || error.message?.includes('interaction_required')) {
					console.log('Redirect error indicates interaction required, redirecting to login...');
					handleAuthFailure();
				}
			}
		});

		return msalInstance;
	} catch (error) {
		console.error('Failed to initialize MSAL instance:', error);
		isMsalInitialized = false;
		throw error;
	}
};

export const getMsalTokenAsync = async () => {
	// Ensure MSAL is initialized before attempting token acquisition
	if (!isMsalInitialized) {
		try {
			await initializeMsalInstance();
		} catch (initError) {
			console.warn('MSAL instance failed to initialize before token acquisition');
			return null;
		}
	}

	// Don't try to initialize again - just check if we have an active account
	if (!msalInstance.getActiveAccount()) {
		console.warn('No active MSAL account found when trying to get token');
		return null;
	}

	try {
		return await msalInstance.acquireTokenSilent(loginRequest);
	} catch (err: any) {
		console.error('Error acquiring MSAL token silently:', err);

		// Check if this is an interaction_required error
		if (
			err.errorCode === 'interaction_required' ||
			err.errorCode === 'consent_required' ||
			err.errorCode === 'login_required' ||
			err.message?.includes('interaction_required')
		) {
			// Clear any existing accounts and redirect to login
			msalInstance.clearCache();
			window.location.href = '/admin/login';
			return null;
		}

		return null;
	}
};

// Function to handle authentication failures and redirect to login
export const handleAuthFailure = () => {
	// Clear any existing accounts and redirect to login
	if (msalInstance) {
		msalInstance.clearCache();
	}
	window.location.href = '/admin/login';
};

// Function to check if the current session is still valid
export const isSessionValid = async (): Promise<boolean> => {
	try {
		if (!isMsalInitialized) {
			await initializeMsalInstance();
		}

		const account = msalInstance.getActiveAccount();
		if (!account) {
			return false;
		}

		// Try to acquire a token silently to verify the session is still valid
		await msalInstance.acquireTokenSilent({
			...loginRequest,
			account: account,
		});

		return true;
	} catch (error: any) {
		// Check if this is an interaction_required error
		if (
			error.errorCode === 'interaction_required' ||
			error.errorCode === 'consent_required' ||
			error.errorCode === 'login_required' ||
			error.message?.includes('interaction_required')
		) {
			msalInstance.clearCache();
			return false;
		}

		return false;
	}
};

export { msalInstance };

// Helpers to acquire specific tokens on demand
export const acquireGraphToken = async () => {
	try {
		if (!isMsalInitialized) {
			await initializeMsalInstance();
		}

		let account = msalInstance.getActiveAccount();
		if (!account) {
			const accounts = msalInstance.getAllAccounts();
			if (accounts.length > 0) {
				account = accounts[0];
				msalInstance.setActiveAccount(account);
			} else {
				return null;
			}
		}

		return msalInstance.acquireTokenSilent({
			...graphRequest,
			account,
		});
	} catch (error) {
		console.error('acquireGraphToken failed:', error);
		return null;
	}
};

export const acquireCustomApiToken = async () => {
	try {
		if (!isMsalInitialized) {
			await initializeMsalInstance();
		}

		let account = msalInstance.getActiveAccount();
		if (!account) {
			const accounts = msalInstance.getAllAccounts();
			if (accounts.length > 0) {
				account = accounts[0];
				msalInstance.setActiveAccount(account);
			} else {
				return null;
			}
		}

		return msalInstance.acquireTokenSilent({
			...customApiRequest,
			account,
		});
	} catch (error) {
		console.error('acquireCustomApiToken failed:', error);
		return null;
	}
};

// Convenience: choose token per resource type
export const getAuthHeaderFor = async (resource: 'graph' | 'custom') => {
	const result = resource === 'graph' ? await acquireGraphToken() : await acquireCustomApiToken();
	if (!result) return {} as Record<string, string>;

	return { Authorization: `Bearer ${result.accessToken}` } as Record<string, string>;
};
